import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyCertificatePage = () => {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/verify/${certificateId}`);
      setCertificate(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Cette attestation n'existe pas ou n'a pas été trouvée.");
      } else {
        setError("Erreur lors de la vérification de l'attestation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Vérification en cours...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <BenevolCheckLogo size="md" />
            </Link>
            <Badge variant="outline" className="text-indigo-600 border-indigo-600">
              Vérification d'attestation
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {error ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-6xl mb-4">❌</div>
                <h2 className="text-xl font-bold text-red-800 mb-2">Attestation non trouvée</h2>
                <p className="text-red-600">{error}</p>
                <p className="text-gray-500 mt-4 text-sm">
                  Si vous pensez qu'il s'agit d'une erreur, veuillez contacter l'association concernée.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : certificate ? (
          <div className="space-y-6">
            {/* Verification Status */}
            <Card className={certificate.status === 'validated' ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
              <CardContent className="pt-6">
                <div className="text-center">
                  {certificate.status === 'validated' ? (
                    <>
                      <div className="text-6xl mb-4">✅</div>
                      <h2 className="text-xl font-bold text-green-800 mb-2">Attestation Vérifiée</h2>
                      <p className="text-green-600">Cette attestation est authentique et a été validée.</p>
                    </>
                  ) : (
                    <>
                      <div className="text-6xl mb-4">⏳</div>
                      <h2 className="text-xl font-bold text-yellow-800 mb-2">Attestation en attente</h2>
                      <p className="text-yellow-600">Cette attestation n'a pas encore été validée.</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Certificate Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-indigo-900">Détails de l'attestation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Bénévole</p>
                    <p className="font-semibold text-gray-900">{certificate.volunteer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Association</p>
                    <p className="font-semibold text-gray-900">{certificate.association_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Période de mission</p>
                    <p className="font-semibold text-gray-900">
                      {formatDate(certificate.start_date)} - {formatDate(certificate.end_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Volume horaire</p>
                    <p className="font-semibold text-gray-900">{certificate.hours_volume} heures</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Mission</p>
                  <p className="text-gray-900">{certificate.mission_description}</p>
                </div>

                {certificate.status === 'validated' && certificate.validation_date && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500">Validée le</p>
                    <p className="font-semibold text-green-700">{formatDate(certificate.validation_date)}</p>
                    <p className="text-sm text-gray-500 mt-1">Par</p>
                    <p className="text-gray-900">{certificate.signatory_name}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info */}
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-800 text-sm">
                Cette page de vérification confirme l'authenticité de l'attestation. 
                Pour obtenir une copie complète du document, veuillez contacter le bénévole ou l'association.
              </AlertDescription>
            </Alert>
          </div>
        ) : null}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Propulsé par France Bénévolat avec l'aide du SDJES Côte d'Or</p>
          <Link to="/" className="text-indigo-600 hover:underline mt-2 inline-block">
            ← Retour à l'accueil
          </Link>
        </div>
      </main>
    </div>
  );
};

export default VerifyCertificatePage;
